import React, { useState, useEffect } from 'react';
import { X, Printer } from 'lucide-react';

const IfadaReceiptModal = ({ payment, apiCall, onClose }) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrderDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      console.log('📥 Loading order details for payment:', payment);
      
      const orderIdMatch = payment.ReferenceNumber?.match(/IFADA-(\d+)/);
      if (orderIdMatch) {
        const orderId = orderIdMatch[1];
        console.log('🔍 Found order ID:', orderId);
        
        const order = await apiCall(`/ifada/orders/${orderId}`, { method: 'GET' });
        console.log('✅ Order details loaded:', order);
        setOrderDetails(order);
      } else {
        console.warn('⚠️ Could not extract order ID from reference:', payment.ReferenceNumber);
        setOrderDetails(null);
      }
    } catch (error) {
      console.error('❌ Error loading order details:', error);
      setOrderDetails(null);
    } finally {
      setLoading(false);
    }
  };

  if (!payment) {
    console.error('❌ No payment data provided');
    return null;
  }

  const formatLBP = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount) + ' ل.ل';
  };

  const translateItemType = (itemType) => {
    const translations = {
      'followup_statement': 'إفادة متابعة',
      'completion_statement': 'إفادة إنهاء',
      'Statement': 'إفادة',
      'Certificate': 'شهادة',
      'University_Documents': 'ملف جامعي',
      'Conduct': 'إفادة حسن سلوك',
      'Difficulty_Process': 'إنجاز ملف الصعوبات التعلمية',
      'Appreciation_BF': 'Appreciation BF',
      'grades': 'علامات مدرسيّة'
    };
    return translations[itemType] || itemType;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=600,height=800');
    
    const itemsRows = orderDetails?.items?.map(item => {
      // ✅ Calculate unit price: either use UnitPrice field or Amount/Quantity
      const unitPrice = item.UnitPrice || (item.Amount / (item.Quantity || 1));
      const total = item.Amount; // Amount is already the total
      
      return `
      <tr>
        <td style="padding: 4px 5px; border-bottom: 1px solid #ddd; font-size: 10px;">
          ${translateItemType(item.ItemType)}
        </td>
        <td style="padding: 4px 5px; border-bottom: 1px solid #ddd; text-align: center; font-size: 10px;">
          ${item.AcademicYear || '-'}
        </td>
        <td style="padding: 4px 5px; border-bottom: 1px solid #ddd; text-align: center; font-size: 10px; font-weight: 600;">
          ${item.Quantity || 1}
        </td>
        <td style="padding: 4px 5px; border-bottom: 1px solid #ddd; text-align: right; font-size: 10px;">
          ${formatLBP(unitPrice)}
        </td>
        <td style="padding: 4px 5px; border-bottom: 1px solid #ddd; text-align: right; font-weight: 600; font-size: 10px;">
          ${formatLBP(total)}
        </td>
      </tr>
    `;
    }).join('') || '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Receipt #${payment.PaymentID}</title>
          <style>
            @page {
              size: A5 portrait;
              margin: 5mm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 8px;
              font-size: 10px;
              line-height: 1.2;
            }
            .header {
              text-align: center;
              margin-bottom: 6px;
              padding-bottom: 5px;
              border-bottom: 2px solid #000;
            }
            .header h1 {
              font-size: 16px;
              margin-bottom: 2px;
            }
            .receipt-type {
              font-size: 9px;
              color: #666;
            }
            .receipt-number {
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              margin: 5px 0;
              padding: 4px;
              background: #f0f0f0;
              border: 2px solid #000;
              border-radius: 3px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 5px 8px;
              margin: 6px 0;
              font-size: 10px;
            }
            .info-item {
              display: flex;
              justify-content: space-between;
              padding: 3px 0;
              border-bottom: 1px dotted #ccc;
            }
            .info-label {
              color: #666;
              font-size: 9px;
            }
            .info-value {
              font-weight: 600;
              font-size: 10px;
            }
            .items-title {
              font-size: 10px;
              font-weight: bold;
              margin: 8px 0 4px 0;
              padding-bottom: 3px;
              border-bottom: 1px solid #000;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 4px 0;
              border: 1px solid #000;
            }
            .items-table thead {
              background: #e8e8e8;
            }
            .items-table th {
              padding: 3px 4px;
              text-align: left;
              font-size: 9px;
              font-weight: 600;
              border-bottom: 1px solid #000;
            }
            .items-table th:nth-child(2),
            .items-table th:nth-child(3) {
              text-align: center;
            }
            .items-table th:nth-child(4),
            .items-table th:nth-child(5) {
              text-align: right;
            }
            .items-table td {
              font-size: 10px;
            }
            .total-section {
              margin: 8px 0;
              padding: 6px;
              border: 2px solid #000;
              border-radius: 3px;
              text-align: center;
            }
            .total-label {
              font-size: 9px;
              margin-bottom: 3px;
              font-weight: 600;
            }
            .total-amount {
              font-size: 18px;
              font-weight: bold;
            }
            .footer {
              margin-top: 8px;
              padding-top: 5px;
              border-top: 1px solid #000;
              text-align: center;
              font-size: 8px;
              color: #666;
            }
            .signature-box {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: 8px;
            }
            .signature-line {
              width: 70px;
              border-top: 1px solid #000;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div class="header">
            <h1>Collège des Apôtres</h1>
            <div class="receipt-type">Ifada Receipt / إيصال إفادة</div>
          </div>

          <!-- Receipt Number -->
          <div class="receipt-number">
            #${payment.PaymentID} - ${new Date(payment.PaymentDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: '2-digit'
            })}
          </div>

          ${orderDetails ? `
            <!-- Student & Payment Info in Grid -->
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Student</span>
                <span class="info-value">${orderDetails.StudentName || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Class</span>
                <span class="info-value">${orderDetails.StudentClass || 'N/A'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Method</span>
                <span class="info-value">${payment.PaymentMethod}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Ref</span>
                <span class="info-value">${payment.ReferenceNumber || 'N/A'}</span>
              </div>
            </div>
          ` : `
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Method</span>
                <span class="info-value">${payment.PaymentMethod}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Reference</span>
                <span class="info-value">${payment.ReferenceNumber || 'N/A'}</span>
              </div>
            </div>
          `}

          ${orderDetails && orderDetails.items && orderDetails.items.length > 0 ? `
            <!-- Items -->
            <div class="items-title">Items / العناصر</div>
            <table class="items-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Year</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
              </tbody>
            </table>
          ` : ''}

          <!-- Total -->
          <div class="total-section">
            <div class="total-label">TOTAL PAID / المبلغ المدفوع</div>
            <div class="total-amount">${formatLBP(payment.Amount)}</div>
          </div>

          <!-- Footer -->
          <div class="footer">
            <div style="margin-bottom: 5px;">Thank you / شكراً</div>
            <div class="signature-box">
              <div>
                <div class="signature-line"></div>
                <div style="font-size: 8px; margin-top: 2px;">Signature</div>
              </div>
              <div style="font-size: 8px;">
                ${new Date().toLocaleString('en-US', {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-3 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-bold text-gray-800">Ifada Receipt</h3>
          <div className="flex gap-2">
            <button 
              onClick={handlePrint}
              className="px-3 py-1.5 bg-gray-800 hover:bg-gray-900 text-white rounded-lg flex items-center gap-2 text-sm"
            >
              <Printer size={14} />
              Print
            </button>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content - Compact Preview */}
        <div className="p-4">
          {/* Header */}
          <div className="text-center mb-3 pb-2 border-b-2 border-gray-800">
            <h1 className="text-lg font-bold">Collège des Apôtres</h1>
            <p className="text-xs text-gray-600">Ifada Receipt / إيصال إفادة</p>
          </div>

          {/* Receipt Number */}
          <div className="text-center mb-3 p-2 bg-gray-100 border-2 border-gray-800 rounded">
            <div className="text-base font-bold">
              #{payment.PaymentID} - {new Date(payment.PaymentDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: '2-digit'
              })}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800 mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Info Grid */}
              {orderDetails && (
                <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-600">Student:</span>
                    <span className="font-semibold">{orderDetails.StudentName}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-600">Class:</span>
                    <span className="font-semibold">{orderDetails.StudentClass || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-600">Method:</span>
                    <span className="font-semibold">{payment.PaymentMethod}</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-600">Ref:</span>
                    <span className="font-semibold text-xs">{payment.ReferenceNumber || 'N/A'}</span>
                  </div>
                </div>
              )}

              {/* Items Table */}
              {orderDetails && orderDetails.items && orderDetails.items.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-bold mb-1 pb-1 border-b border-gray-800">Items / العناصر</h4>
                  <table className="w-full text-xs border border-gray-800">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-1 py-1 text-left text-xs border-b border-gray-800">Type</th>
                        <th className="px-1 py-1 text-center text-xs border-b border-gray-800">Yr</th>
                        <th className="px-1 py-1 text-center text-xs border-b border-gray-800">Q</th>
                        <th className="px-1 py-1 text-right text-xs border-b border-gray-800">Price</th>
                        <th className="px-1 py-1 text-right text-xs border-b border-gray-800">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.items.map((item, index) => {
                        // ✅ Calculate unit price: either use UnitPrice field or Amount/Quantity
                        const unitPrice = item.UnitPrice || (item.Amount / (item.Quantity || 1));
                        const total = item.Amount; // Amount is already the total
                        
                        return (
                        <tr key={index} className="border-b border-gray-300">
                          <td className="px-1 py-1 text-xs">{translateItemType(item.ItemType)}</td>
                          <td className="px-1 py-1 text-center text-xs">{item.AcademicYear}</td>
                          <td className="px-1 py-1 text-center font-semibold text-xs">{item.Quantity || 1}</td>
                          <td className="px-1 py-1 text-right text-xs">{formatLBP(unitPrice)}</td>
                          <td className="px-1 py-1 text-right font-semibold text-xs">{formatLBP(total)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* Total */}
          <div className="border-2 border-gray-800 rounded p-2 text-center mb-3">
            <div className="text-xs font-semibold mb-1">TOTAL PAID / المبلغ المدفوع</div>
            <div className="text-xl font-bold">{formatLBP(payment.Amount)}</div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-gray-600 border-t border-gray-800 pt-2">
            <p className="mb-2">Thank you / شكراً</p>
            <div className="flex justify-between items-center text-xs">
              <div>
                <div className="w-16 border-t border-gray-800 mb-1"></div>
                <div className="text-xs">Signature</div>
              </div>
              <div className="text-xs">{new Date().toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IfadaReceiptModal;